(() => {
  if (window.__olasSmartRecommendationsLoaded) return;
  window.__olasSmartRecommendationsLoaded = true;

  const toHandle = value => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const pipeContains = (pipeString, value) => {
    const handle = toHandle(value);
    if (!handle) return false;
    return String(pipeString || '').includes(`|${handle}|`);
  };

  const getOptionWrappers = selector => Array.from(selector.querySelectorAll('[data-input-wrapper]'));

  const getColorWrapper = selector => {
    const wrappers = getOptionWrappers(selector);

    return wrappers.find(wrapper => {
      const idHandle = toHandle(wrapper.id || '');
      const legend = wrapper.querySelector('legend');
      const legendHandle = toHandle(legend ? legend.textContent : '');

      return idHandle.includes('color') ||
        idHandle.includes('colour') ||
        legendHandle === 'color' ||
        legendHandle === 'colour';
    }) || null;
  };

  const getSelectedColor = selector => {
    const wrapper = getColorWrapper(selector);
    if (!wrapper) return '';

    const checked = wrapper.querySelector('input[type="radio"]:checked');
    if (checked) return checked.value || '';

    const select = wrapper.querySelector('select');
    if (select) return select.value || '';

    return '';
  };

  const parseVariants = slide => {
    if (slide.__olasVariants) return slide.__olasVariants;

    const script = slide.querySelector('.js-olas-smart-recommendation-variants');
    if (!script) {
      slide.__olasVariants = [];
      return slide.__olasVariants;
    }

    try {
      slide.__olasVariants = JSON.parse(script.textContent || '[]');
    } catch (error) {
      console.warn('[Las Olas] Could not parse smart recommendation variant data.', error);
      slide.__olasVariants = [];
    }

    return slide.__olasVariants;
  };



  const parseColorMedia = slide => {
    if (slide.__olasColorMedia) return slide.__olasColorMedia;

    const script = slide.querySelector('.js-olas-smart-recommendation-media');
    if (!script) {
      slide.__olasColorMedia = [];
      return slide.__olasColorMedia;
    }

    try {
      slide.__olasColorMedia = JSON.parse(script.textContent || '[]');
    } catch (error) {
      console.warn('[Las Olas] Could not parse smart recommendation media data.', error);
      slide.__olasColorMedia = [];
    }

    return slide.__olasColorMedia;
  };

  const findBestMediaForColor = (mediaItems, colorHandle) => {
    if (!mediaItems.length || !colorHandle) return null;
    return mediaItems.find(item => item.colorHandle === colorHandle) || null;
  };

  const findBestVariantForColor = (variants, colorHandle) => {
    if (!variants.length || !colorHandle) return null;

    const matches = variants.filter(variant => variant.colorHandle === colorHandle);
    if (!matches.length) return null;

    return matches.find(variant => variant.available) || matches[0] || null;
  };

  class OlasSmartRecommendations extends HTMLElement {
    constructor() {
      super();

      this.slider = this.querySelector('[data-olas-smart-slider]');
      this.wrapper = this.querySelector('[data-olas-smart-wrapper]');
      this.emptyMessage = this.querySelector('[data-olas-smart-empty]');
      this.buttons = this.querySelector('[data-olas-smart-buttons]');
      this.maxProducts = Number(this.dataset.maxProducts || 8);
      this.fillFromSource = this.dataset.fillFromSource === 'true';
      this.imageHoverEnabled = this.dataset.imageHoverEnabled !== 'false';
      this.currentVendor = this.dataset.currentVendorHandle || '';
      this.currentType = this.dataset.currentTypeHandle || '';
      this.currentPiece = this.dataset.currentPieceGroup || 'other';
      this.oppositePiece = this.dataset.oppositePieceGroup || '';
      this.activeColor = toHandle(this.dataset.initialColorHandle || this.dataset.initialColor || '');
      this.allSlides = [];
      this.swiper = null;
      this.hasInitialized = false;
      this.handleDocumentChange = this.handleDocumentChange.bind(this);
      this.handleMediaRefresh = this.handleMediaRefresh.bind(this);
    }

    connectedCallback() {
      if (this.hasInitialized || !this.wrapper) return;

      this.allSlides = Array.from(this.wrapper.querySelectorAll('.js-olas-smart-recommendation-slide'));
      this.hasInitialized = true;
      this.render();

      document.addEventListener('change', this.handleDocumentChange);
      document.addEventListener('olas:product-media-refreshed', this.handleMediaRefresh);
      document.addEventListener('shopify:section:load', () => this.render());
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.handleDocumentChange);
      document.removeEventListener('olas:product-media-refreshed', this.handleMediaRefresh);
      this.destroySlider();
    }

    handleDocumentChange(event) {
      const selector = event.target.closest('product-selector');
      if (!selector) return;

      window.setTimeout(() => {
        const currentSelector = document.querySelector(`product-selector[data-section-id="${selector.dataset.sectionId}"]`) || selector;
        const selectedColor = getSelectedColor(currentSelector);
        const selectedColorHandle = toHandle(selectedColor);

        if (!selectedColorHandle || selectedColorHandle === this.activeColor) return;

        this.activeColor = selectedColorHandle;
        this.render();
      }, 0);
    }

    handleMediaRefresh(event) {
      const selectedColorHandle = toHandle(event.detail?.selectedColor || '');
      if (!selectedColorHandle || selectedColorHandle === this.activeColor) return;

      this.activeColor = selectedColorHandle;
      this.render();
    }

    getRank(slide) {
      const hasSameColor = pipeContains(slide.dataset.colors, this.activeColor);
      const sameVendor = slide.dataset.vendorHandle === this.currentVendor;
      const sameType = slide.dataset.typeHandle === this.currentType;
      const oppositePiece = this.oppositePiece && slide.dataset.pieceGroup === this.oppositePiece;
      const samePiece = this.currentPiece !== 'other' && slide.dataset.pieceGroup === this.currentPiece;

      if (sameVendor && hasSameColor && oppositePiece) return 10;
      if (sameVendor && hasSameColor) return 20;
      if (hasSameColor && oppositePiece) return 30;
      if (sameType) return 40;
      if (samePiece) return 50;

      return this.fillFromSource ? 90 : 999;
    }

    updateCardForColor(slide) {
      const variants = parseVariants(slide);
      const colorMedia = findBestMediaForColor(parseColorMedia(slide), this.activeColor);
      const variant = findBestVariantForColor(variants, this.activeColor);
      const mediaLink = slide.querySelector('[data-olas-card-link]');
      const contentLink = slide.querySelector('[data-olas-card-content-link]');
      const image = slide.querySelector('.js-olas-card-image');
      let hoverImage = slide.querySelector('.js-olas-card-hover-image');
      const media = slide.querySelector('.olas-smart-recommendations__media');
      const fallbackUrl = slide.dataset.fallbackUrl || '#';
      const fallbackImage = slide.dataset.fallbackImage || '';
      const nextUrl = variant?.url || fallbackUrl;
      const nextImage = colorMedia?.image || variant?.image || fallbackImage;
      const nextHoverImage = this.imageHoverEnabled ? colorMedia?.hoverImage || '' : '';

      if (mediaLink) mediaLink.href = nextUrl;
      if (contentLink) contentLink.href = nextUrl;

      if (image && nextImage && image.getAttribute('src') !== nextImage) {
        media?.classList.add('is-updating');
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = nextImage;
        image.addEventListener('load', () => media?.classList.remove('is-updating'), { once: true });
      } else {
        media?.classList.remove('is-updating');
      }

      if (!this.imageHoverEnabled) {
        media?.classList.remove('has-hover');
        if (hoverImage) {
          hoverImage.removeAttribute('srcset');
          hoverImage.removeAttribute('sizes');
          hoverImage.removeAttribute('src');
          hoverImage.setAttribute('aria-hidden', 'true');
        }
        return;
      }

      if (nextHoverImage) {
        if (!hoverImage && media) {
          hoverImage = document.createElement('img');
          hoverImage.className = 'js-olas-card-hover-image hover-img';
          hoverImage.loading = 'lazy';
          hoverImage.setAttribute('aria-hidden', 'true');
          media.appendChild(hoverImage);
        }

        if (hoverImage) {
          hoverImage.removeAttribute('srcset');
          hoverImage.removeAttribute('sizes');
          hoverImage.alt = image?.alt || '';
          if (hoverImage.getAttribute('src') !== nextHoverImage) {
            hoverImage.src = nextHoverImage;
          }
          media?.classList.add('has-hover');
        }
      } else {
        media?.classList.remove('has-hover');
        if (hoverImage) {
          hoverImage.removeAttribute('srcset');
          hoverImage.removeAttribute('sizes');
          hoverImage.removeAttribute('src');
          hoverImage.setAttribute('aria-hidden', 'true');
        }
      }
    }

    getRankedSlides() {
      return this.allSlides
        .map((slide, originalIndex) => ({
          slide,
          originalIndex,
          rank: this.getRank(slide)
        }))
        .filter(item => item.rank < 999)
        .sort((a, b) => {
          if (a.rank !== b.rank) return a.rank - b.rank;
          return a.originalIndex - b.originalIndex;
        })
        .slice(0, this.maxProducts);
    }

    render() {
      if (!this.wrapper) return;

      const rankedSlides = this.getRankedSlides();
      this.destroySlider();
      this.wrapper.innerHTML = '';

      rankedSlides.forEach((item, index) => {
        const slide = item.slide;
        slide.classList.remove('is-hidden');
        slide.style.setProperty('--olas-recommendation-order', index + 1);
        slide.dataset.olasRank = item.rank;
        this.updateCardForColor(slide);
        this.wrapper.appendChild(slide);
      });

      const isEmpty = rankedSlides.length === 0;
      this.slider?.classList.toggle('is-empty', isEmpty);
      this.emptyMessage?.classList.toggle('hidden', !isEmpty);
      this.buttons?.classList.toggle('hidden', isEmpty || rankedSlides.length <= 4);

      if (!isEmpty) this.initSlider();
    }

    initSlider() {
      if (!this.slider || typeof Swiper === 'undefined') return;

      let swiperOptions = {};
      try {
        swiperOptions = JSON.parse(this.slider.getAttribute('data-swiper-options') || '{}');
      } catch (error) {
        swiperOptions = {};
      }

      this.swiper = new Swiper(this.slider, {
        a11y: false,
        threshold: 3,
        spaceBetween: swiperOptions.spaceBetweenMobile || 2,
        slidesPerView: 'auto',
        resistanceRatio: 0.72,
        navigation: {
          nextEl: this.slider.querySelector('.swiper-button--next'),
          prevEl: this.slider.querySelector('.swiper-button--prev')
        },
        breakpoints: {
          750: {
            slidesPerView: 3,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2
          },
          1100: {
            slidesPerView: 4,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2
          }
        }
      });
    }

    destroySlider() {
      if (this.swiper && !this.swiper.destroyed) {
        this.swiper.destroy(true, true);
      }
      this.swiper = null;
    }
  }

  if (!customElements.get('olas-smart-recommendations')) {
    customElements.define('olas-smart-recommendations', OlasSmartRecommendations);
  }
})();
